import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import interiorLibraryAdminApi from "../services/interiorLibraryAdminApi";

const EMPTY_TEXTURE = {
  texture_role: "color",
  file_url: "",
  real_width_mm: 2800,
  real_height_mm: 2070,
  rotation_deg: 0,
  scale_x: 1,
  scale_y: 1,
  offset_x: 0,
  offset_y: 0,
  wrap_s: "repeat",
  wrap_t: "repeat",
  is_primary: true,
};

export default function InteriorMaterialEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [tab, setTab] = useState("general");
  const [categories, setCategories] = useState([]);
  const [material, setMaterial] = useState(null);
  const [textureForm, setTextureForm] = useState(EMPTY_TEXTURE);
  const [editingTextureId, setEditingTextureId] = useState(null);

  async function load() {
    const [matRes, catRes] = await Promise.all([
      interiorLibraryAdminApi.getMaterial(id),
      interiorLibraryAdminApi.listCategories({ scope: "material" }),
    ]);

    setMaterial(matRes.item);
    setCategories(catRes.items || []);
  }

  useEffect(() => {
    load();
  }, [id]);

  function updateField(key, value) {
    setMaterial((p) => ({ ...p, [key]: value }));
  }

  async function saveGeneral() {
    await interiorLibraryAdminApi.updateMaterial(id, material);
    await load();
    alert("Material saved.");
  }

  async function addTexture() {
  if (!textureForm.file_url) {
    alert("Upload texture image first.");
    return;
  }

  if (editingTextureId) {
    await interiorLibraryAdminApi.updateMaterialTexture(
      editingTextureId,
      textureForm
    );
    setEditingTextureId(null);
  } else {
    await interiorLibraryAdminApi.createMaterialTexture(id, textureForm);
  }

  setTextureForm(EMPTY_TEXTURE);
  await load();
}

  async function deleteTexture(textureId) {
    if (!window.confirm("Delete texture?")) return;
    await interiorLibraryAdminApi.deleteMaterialTexture(textureId);
    await load();
  }

  async function uploadTextureFile(file) {
  if (!file) return;

  try {
    const res = await interiorLibraryAdminApi.uploadMaterialTexture(file);

    setTextureForm((p) => ({
      ...p,
      file_url: res.file_url || "",
    }));
  } catch (err) {
    console.error("upload texture error:", err);
    alert(err?.response?.data?.message || "Texture upload failed.");
  }
}

  if (!material) {
    return <div className="p-4">Loading material...</div>;
  }

  return (
    <div className="p-4 space-y-4 bg-slate-50 min-h-screen">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">{material.name}</h1>
          <div className="text-sm text-slate-500">{material.material_key}</div>
        </div>

        <button
          onClick={() => navigate("/interior-editor/materials")}
          className="px-3 py-2 border rounded bg-white"
        >
          Back
        </button>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => setTab("general")}
          className={`px-3 py-2 rounded border ${
            tab === "general" ? "bg-blue-600 text-white" : "bg-white"
          }`}
        >
          General
        </button>

        <button
          onClick={() => setTab("textures")}
          className={`px-3 py-2 rounded border ${
            tab === "textures" ? "bg-blue-600 text-white" : "bg-white"
          }`}
        >
          Textures
        </button>

        <button
          onClick={() => setTab("suppliers")}
          className={`px-3 py-2 rounded border ${
            tab === "suppliers" ? "bg-blue-600 text-white" : "bg-white"
          }`}
        >
          Suppliers
        </button>
      </div>

      {tab === "general" && (
        <div className="bg-white border rounded p-4 space-y-3 max-w-3xl">
          <input
            value={material.material_key || ""}
            onChange={(e) => updateField("material_key", e.target.value)}
            placeholder="Material key"
            className="w-full px-3 py-2 border rounded"
          />

          <input
            value={material.name || ""}
            onChange={(e) => updateField("name", e.target.value)}
            placeholder="Name"
            className="w-full px-3 py-2 border rounded"
          />

          <select
            value={material.category || ""}
            onChange={(e) => updateField("category", e.target.value)}
            className="w-full px-3 py-2 border rounded bg-white"
          >
            <option value="">Select category</option>
            {categories.map((c) => (
              <option key={c.id} value={c.category_key}>
                {c.label}
              </option>
            ))}
          </select>

          <div className="grid grid-cols-2 gap-3">
            <input
              value={material.manufacturer || ""}
              onChange={(e) => updateField("manufacturer", e.target.value)}
              placeholder="Manufacturer"
              className="px-3 py-2 border rounded"
            />

            <input
              value={material.collection_name || ""}
              onChange={(e) => updateField("collection_name", e.target.value)}
              placeholder="Collection"
              className="px-3 py-2 border rounded"
            />

            <input
              value={material.decor_code || ""}
              onChange={(e) => updateField("decor_code", e.target.value)}
              placeholder="Decor code"
              className="px-3 py-2 border rounded"
            />

            <input
              value={material.decor_name || ""}
              onChange={(e) => updateField("decor_name", e.target.value)}
              placeholder="Decor name"
              className="px-3 py-2 border rounded"
            />

            <input
              value={material.base_color || ""}
              onChange={(e) => updateField("base_color", e.target.value)}
              placeholder="Base color e.g. #d8c3a5"
              className="px-3 py-2 border rounded"
            />

            <input
              value={material.finish || ""}
              onChange={(e) => updateField("finish", e.target.value)}
              placeholder="Finish e.g. matt / gloss / wood"
              className="px-3 py-2 border rounded"
            />

            <input
              value={material.texture_type || ""}
              onChange={(e) => updateField("texture_type", e.target.value)}
              placeholder="Texture type e.g. wood / stone / solid"
              className="px-3 py-2 border rounded"
            />

            <input
              type="number"
              value={material.default_thickness || ""}
              onChange={(e) =>
                updateField("default_thickness", Number(e.target.value))
              }
              placeholder="Default thickness"
              className="px-3 py-2 border rounded"
            />

            <input
              value={material.unit || "m2"}
              onChange={(e) => updateField("unit", e.target.value)}
              placeholder="Unit"
              className="px-3 py-2 border rounded"
            />
          </div>

          <textarea
            value={material.notes || ""}
            onChange={(e) => updateField("notes", e.target.value)}
            placeholder="Notes"
            className="w-full px-3 py-2 border rounded h-24"
          />

          <label className="flex gap-2 items-center text-sm">
            <input
              type="checkbox"
              checked={!!material.is_active}
              onChange={(e) => updateField("is_active", e.target.checked)}
            />
            Active
          </label>

          <button
            onClick={saveGeneral}
            className="px-4 py-2 bg-blue-600 text-white rounded"
          >
            Save General
          </button>
        </div>
      )}

      {tab === "textures" && (
        <div className="grid grid-cols-[420px_1fr] gap-4">
          <div className="bg-white border rounded p-4 space-y-3">
            <h2 className="font-semibold">{editingTextureId ? "Save Texture" : "Add Texture"}</h2>

            <select
              value={textureForm.texture_role}
              onChange={(e) =>
                setTextureForm((p) => ({
                  ...p,
                  texture_role: e.target.value,
                }))
              }
              className="w-full px-3 py-2 border rounded bg-white"
            >
              <option value="color">Color / Albedo</option>
              <option value="normal">Normal</option>
              <option value="roughness">Roughness</option>
              <option value="metalness">Metalness</option>
              <option value="ao">Ambient Occlusion</option>
              <option value="preview">Preview</option>
            </select>

            <div className="space-y-1">
  <label className="text-xs text-slate-500">
    Upload texture image
  </label>

  <input
    type="file"
    accept="image/png,image/jpeg,image/webp"
    onChange={(e) => uploadTextureFile(e.target.files?.[0])}
    className="w-full px-3 py-2 border rounded bg-white"
  />

  <div className="text-xs text-slate-400">
    Upload JPG, PNG or WEBP. File URL will be filled automatically.
  </div>
</div>

            <div className="grid grid-cols-2 gap-2">
  <div>
    <label className="text-xs text-slate-500">Rotation deg</label>
    <input
      type="number"
      value={textureForm.rotation_deg}
      onChange={(e) =>
        setTextureForm((p) => ({
          ...p,
          rotation_deg: Number(e.target.value),
        }))
      }
      className="w-full px-3 py-2 border rounded"
    />
  </div>

  <div>
    <label className="text-xs text-slate-500">Scale X</label>
    <input
      type="number"
      value={textureForm.scale_x}
      onChange={(e) =>
        setTextureForm((p) => ({
          ...p,
          scale_x: Number(e.target.value),
        }))
      }
      className="w-full px-3 py-2 border rounded"
    />
  </div>

  <div>
    <label className="text-xs text-slate-500">Scale Y</label>
    <input
      type="number"
      value={textureForm.scale_y}
      onChange={(e) =>
        setTextureForm((p) => ({
          ...p,
          scale_y: Number(e.target.value),
        }))
      }
      className="w-full px-3 py-2 border rounded"
    />
  </div>

  <div>
    <label className="text-xs text-slate-500">Offset X</label>
    <input
      type="number"
      value={textureForm.offset_x}
      onChange={(e) =>
        setTextureForm((p) => ({
          ...p,
          offset_x: Number(e.target.value),
        }))
      }
      className="w-full px-3 py-2 border rounded"
    />
  </div>

  <div>
    <label className="text-xs text-slate-500">Offset Y</label>
    <input
      type="number"
      value={textureForm.offset_y}
      onChange={(e) =>
        setTextureForm((p) => ({
          ...p,
          offset_y: Number(e.target.value),
        }))
      }
      className="w-full px-3 py-2 border rounded"
    />
  </div>
</div>

<div className="grid grid-cols-2 gap-2">
  <div>
    <label className="text-xs text-slate-500">Real width (mm)</label>
    <input
      type="number"
      value={textureForm.real_width_mm || ""}
      onChange={(e) =>
        setTextureForm((p) => ({
          ...p,
          real_width_mm: Number(e.target.value),
        }))
      }
      className="w-full px-3 py-2 border rounded"
    />
  </div>

  <div>
    <label className="text-xs text-slate-500">Real height (mm)</label>
    <input
      type="number"
      value={textureForm.real_height_mm || ""}
      onChange={(e) =>
        setTextureForm((p) => ({
          ...p,
          real_height_mm: Number(e.target.value),
        }))
      }
      className="w-full px-3 py-2 border rounded"
    />
  </div>
</div>

            <div className="grid grid-cols-2 gap-2">
              <select
                value={textureForm.wrap_s}
                onChange={(e) =>
                  setTextureForm((p) => ({ ...p, wrap_s: e.target.value }))
                }
                className="px-3 py-2 border rounded bg-white"
              >
                <option value="repeat">Repeat S</option>
                <option value="clamp">Clamp S</option>
              </select>

              <select
                value={textureForm.wrap_t}
                onChange={(e) =>
                  setTextureForm((p) => ({ ...p, wrap_t: e.target.value }))
                }
                className="px-3 py-2 border rounded bg-white"
              >
                <option value="repeat">Repeat T</option>
                <option value="clamp">Clamp T</option>
              </select>
            </div>

            <label className="flex gap-2 items-center text-sm">
              <input
                type="checkbox"
                checked={!!textureForm.is_primary}
                onChange={(e) =>
                  setTextureForm((p) => ({
                    ...p,
                    is_primary: e.target.checked,
                  }))
                }
              />
              Primary texture
            </label>

            <button
              onClick={addTexture}
              className="px-4 py-2 bg-blue-600 text-white rounded"
            >
              {editingTextureId ? "Save Texture" : "Add Texture"}
            </button>
          </div>

          <div className="bg-white border rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="p-2 text-left">Role</th>
                  <th className="p-2 text-left">File</th>
                  <th className="p-2 text-left">Transform</th>
                  <th className="p-2 text-left">Preview</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {(material.textures || []).map((t) => (
                  <tr key={t.id} className="border-t align-top">
                    <td className="p-2">
                      {t.texture_role}
                      {t.is_primary ? (
                        <div className="text-xs text-blue-600">primary</div>
                      ) : null}
                    </td>

                    <td className="p-2 break-all">{t.file_url}</td>

                    <td className="p-2 text-xs">
  real: {t.real_width_mm || "—"} x {t.real_height_mm || "—"} mm <br />
  rot: {t.rotation_deg}° <br />
  scale: {t.scale_x} / {t.scale_y} <br />
  offset: {t.offset_x} / {t.offset_y}
</td>

                    <td className="p-2">
                      {t.file_url ? (
                        <img
                          src={
  String(t.file_url || "").startsWith("http")
    ? t.file_url
    : `https://modularobjects.com/mailserver${t.file_url}`
}
                          alt=""
                          className="w-20 h-20 object-cover border rounded"
                        />
                      ) : (
                        "—"
                      )}
                    </td>

                    <button
  onClick={() => {
    setEditingTextureId(t.id);
    setTextureForm({
      texture_role: t.texture_role || "color",
      file_url: t.file_url || "",
      real_width_mm: Number(t.real_width_mm || 2700),
      real_height_mm: Number(t.real_height_mm || 2000),
      rotation_deg: Number(t.rotation_deg || 0),
      scale_x: Number(t.scale_x || 1),
      scale_y: Number(t.scale_y || 1),
      offset_x: Number(t.offset_x || 0),
      offset_y: Number(t.offset_y || 0),
      wrap_s: t.wrap_s || "repeat",
      wrap_t: t.wrap_t || "repeat",
      is_primary: Number(t.is_primary) === 1 || t.is_primary === true,
    });
  }}
  className="px-2 py-1 border rounded text-blue-600 mr-2"
>
  Edit
</button>

                    <td className="p-2">
                      <button
                        onClick={() => deleteTexture(t.id)}
                        className="px-2 py-1 border rounded text-red-600"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}

                {!material.textures?.length && (
                  <tr>
                    <td colSpan="5" className="p-4 text-slate-400">
                      No textures.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "suppliers" && (
        <div className="bg-white border rounded p-4 text-slate-500">
          Supplier editor comes next.
        </div>
      )}
    </div>
  );
}